<template>
  <div class="wrapper__content-sale-document col-container">
    <div class="col-header">
      <div class="col-header-left">
        <ButtonCirclePlusComponent
          v-if="$canOpen('sales.quotes.add')"
          @onClick="quoteAdd"
        />
        <span class="col-title">Cotizaciones</span>
      </div>
      <div class="col-actions">
        <ButtonFilterComponent
          @onChangeFilters="onChangeFilters"
          @onApplyFilters="onApplyFilters"
          :setFilters="searchFilters"
          :loading="loading"
          :setStatus="filterStatus"
          placeholder="Buscar por número documento"
          isStatus
          isSearch
          isOrderBy
          isDates
          isClients
          isTitle
          class="mx-2"
        />
      </div>
    </div>
    <template>
      <WrapperScrollbarComponent @scrollingEnd="infiniteHandler">
        <QuoteCardComponent
          v-for="(quote, index) in filterQuotes"
          :key="index"
          :quote="quote"
        />
        <infinite-loading
          @distance="90"
          @infinite="infiniteHandler"
          ref="infiniteLoading"
        >
          <div slot="spinner">
            <SkeletonOneCardComponent />
          </div>
          <div slot="no-more"></div>
          <div slot="no-results">
            <QuoteEmptyComponent v-if="!quotes.length" />
          </div>
        </infinite-loading>
      </WrapperScrollbarComponent>
    </template>
  </div>
</template>

<script>
import ButtonCirclePlusComponent from "../../../components/ButtonCirclePlus";
import ButtonFilterComponent from "../../../components/DrawerFilter";
import QuoteEmptyComponent from "./Empty";
import QuoteCardComponent from "./QuoteCard";
import SkeletonOneCardComponent from "@/components/Animations/Loading/SkeletonOneCard";
import WrapperScrollbarComponent from "../../../../components/WrapperScrollbar";

import quoteService from "../services/quoteService";
import { signingEventBus } from "@/services/signingEventBus";

export default {
  name: "QuoteColumnsComponent",
  data() {
    return {
      loading: true,
      searchFilters: {},
      filterStatus: [
        {
          label: "Borrador",
          key: "draft"
        },
        {
          label: "Pendiente",
          key: "pending"
        },
        {
          label: "Preaprobada",
          key: "preapproved"
        },
        {
          label: "Aprobada",
          key: "success"
        },
        {
          label: "No aprobada",
          key: "deny"
        }
      ],
      page: 1,
      quotes: [],
      breakScroll: false,
      debounceTimer: null
    };
  },
  watch: {
    notication(newVal, oldVal) {
      if (oldVal === undefined) return;
      if (!newVal) return;
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.onAsyncDataForDates();
      }, 200);
    }
  },
  computed: {
    notication() {
      return this.$store.getters.getNotification;
    },
    filterOrderBy() {
      const { orderBy } = this.searchFilters;
      return orderBy;
    },
    requestFilters() {
      const {
        search,
        from,
        to,
        status,
        clientId: client,
        title
      } = this.searchFilters;
      // Only include defined/non-null values to prevent API confusion
      const filters = {};
      if (search) filters.search = search;
      if (from) filters.from = from;
      if (to) filters.to = to;
      if (status) filters.status = status;
      if (client) filters.client = client;
      if (title) filters.title = title;
      return filters;
    },
    filterQuotes() {
      return this.quotes.concat().sort((a, b) => {
        const versionA = a.versions ? a.versions[0] : null;
        const versionB = b.versions ? b.versions[0] : null;
        if (this.filterOrderBy && this.filterOrderBy === "desc") {
          if (!versionA || !versionB) return 0;
          return versionA.total > versionB.total ? -1 : 1;
        } else if (this.filterOrderBy && this.filterOrderBy === "asc") {
          if (!versionA || !versionB) return 0;
          return versionA.total < versionB.total ? -1 : 1;
        }
        // Default: sort by date descending (most recent first)
        const dateA =
          versionA && versionA.created_at
            ? new Date(versionA.created_at)
            : new Date(0);
        const dateB =
          versionB && versionB.created_at
            ? new Date(versionB.created_at)
            : new Date(0);
        return dateB - dateA;
      });
    },
    getQuotesFilters() {
      const { documents } = this.$store.getters.getModule("sales");
      return documents.quotes.filters.get;
    }
  },
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    quoteAdd() {
      this.goRouter("sales.quotes.add");
    },
    updatedSearchFilters(filters) {
      this.searchFilters = {
        ...this.searchFilters,
        ...filters
      };

      this.$store.dispatch("salesDocumentsFiltersGet", {
        documentKey: "quotes",
        filters: this.searchFilters
      });
    },
    onChangeFilters(filters) {
      this.updatedSearchFilters(filters);
    },
    onApplyFilters() {
      this.onAsyncDataForDates();
    },
    onAsyncDataForDates() {
      this.quotes = [];
      this.page = 1;
      // El aviso de refresco llega con debounce: el componente puede haberse
      // desmontado (navegación) antes de que corra.
      if (!this.$refs.infiniteLoading) return;
      this.$refs.infiniteLoading.stateChanger.reset();
      this.$store.dispatch("sendNotification", false);
    },
    async getQuotes($state) {
      try {
        this.loading = true;
        let response = await quoteService
          .getQuotes({
            params: {
              ...{
                page: this.page,
                client: this.getClientId
              },
              ...this.requestFilters
            }
          })
          .finally(() => {
            this.breakScroll = false;
            this.loading = false;
          });
        const { quotes } = response.data || {};
        // El interceptor de Api.js "silencia" los GET cancelados por deduplicación
        // devolviendo { data: {} }. Sin este guard, quotes.length revienta, el catch
        // se lo traga y $state nunca se resuelve: el infinite-loading queda colgado
        // y la columna solo vuelve a cargar recargando la página entera.
        if (!Array.isArray(quotes)) {
          $state.loaded();
          return;
        }

        if (quotes.length) {
          if (this.page === 1) {
            this.quotes = quotes;
          } else {
            // Deduplicate: only push items not already in the list
            const existingIds = new Set(this.quotes.map(q => q.id));
            const newItems = quotes.filter(item => !existingIds.has(item.id));
            if (newItems.length === 0) {
              // All returned items are already shown — no more unique data
              $state.complete();
              return;
            }
            newItems.forEach(item => this.quotes.push(item));
          }
          this.page = this.page + 1;
          $state.loaded();
        } else {
          $state.complete();
        }
      } catch (error) {
        return false;
      }
    },
    onQuoteStatusChanged({ quoteId, status }) {
      if (!quoteId) return;
      const index = this.quotes.findIndex(q => q.id === quoteId);
      if (index !== -1) {
        this.$set(this.quotes[index], "status", status);
      }
    },
    infiniteHandler($state) {
      if (!$state || typeof $state.complete !== "function") return;
      if (this.breakScroll) return false;
      this.breakScroll = true;
      this.getQuotes($state);
    }
  },
  components: {
    ButtonCirclePlusComponent,
    ButtonFilterComponent,
    QuoteEmptyComponent,
    QuoteCardComponent,
    SkeletonOneCardComponent,
    WrapperScrollbarComponent
  },
  props: {
    getClientId: {
      default: null
    }
  },
  mounted() {
    this.searchFilters = this.getQuotesFilters;
    signingEventBus.$on("quote:status-changed", this.onQuoteStatusChanged);
  },
  beforeDestroy() {
    signingEventBus.$off("quote:status-changed", this.onQuoteStatusChanged);
  }
};
</script>

<style lang="scss" scoped></style>
